import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getProjects, createProject, deleteProject } from "../api/client";
import Modal from "../components/Modal";
import NotificationsBell from "../components/NotificationsBell";
import { useNotificationsRealtime } from "../hooks/useNotificationsRealtime";
import "./ProjectList.css";

function ProjectList() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [newProjectDescription, setNewProjectDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchProjects();
  }, []);

  // Subscribe to notifications on visible project channels
  useNotificationsRealtime(projects.map((p) => p.id));

  const fetchProjects = async () => {
    try {
      const data = await getProjects();
      setProjects(data);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);

    try {
      const newProject = await createProject({
        name: newProjectName,
        description: newProjectDescription,
      });
      setProjects([...projects, newProject]);
      setShowCreateModal(false);
      setNewProjectName("");
      setNewProjectDescription("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project?")) {
      return;
    }

    try {
      await deleteProject(projectId);
      setProjects(projects.filter((p) => p.id !== projectId));
    } catch (err) {
      alert(err.response?.data?.error || "Failed to delete project");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (loading) {
    return <div className="loading-container">Loading projects...</div>;
  }

  return (
    <div className="project-list-page">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <Link to="/projects" className="logo">
              Q&A
            </Link>
            <div className="nav">
              <NotificationsBell />
              {user && <span className="user-info">{user.name}</span>}
              {user && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={handleLogout}
                  id="logout-button"
                  name="logout-button"
                >
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="container">
        <div className="page-header">
          <h1 className="page-title">Projects</h1>
          <button
            className="btn"
            onClick={() => setShowCreateModal(true)}
            id="create-project-button"
            name="create-project-button"
          >
            + New Project
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="empty-state">
            <p>No projects yet. Create your first project to get started.</p>
          </div>
        ) : (
          <div className="projects-grid">
            {projects.map((project) => (
              <div
                key={project.id}
                className="project-card"
                id={`project-${project.id}`}
              >
                <Link
                  to={`/projects/${project.id}`}
                  className="project-card-link"
                >
                  <h3 className="project-card-title">{project.name}</h3>
                  {project.description && (
                    <p className="project-card-description">
                      {project.description}
                    </p>
                  )}
                </Link>
                <button
                  className="project-delete-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteProject(project.id);
                  }}
                  id={`delete-project-${project.id}`}
                  name={`delete-project-${project.id}`}
                  title="Delete project"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {showCreateModal && (
        <Modal
          onClose={() => setShowCreateModal(false)}
          title="Create New Project"
        >
          {error && <div className="error-message">{error}</div>}
          <form
            onSubmit={handleCreateProject}
            id="create-project-form"
            name="create-project-form"
          >
            <div className="form-group">
              <label htmlFor="project-name" className="form-label">
                Project Name *
              </label>
              <input
                type="text"
                id="project-name"
                name="project-name"
                className="input"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="Enter project name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="project-description" className="form-label">
                Description
              </label>
              <textarea
                id="project-description"
                name="project-description"
                className="textarea"
                value={newProjectDescription}
                onChange={(e) => setNewProjectDescription(e.target.value)}
                placeholder="Enter project description (optional)"
              />
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn"
                disabled={creating}
                id="submit-create-project"
                name="submit-create-project"
              >
                {creating && <span className="spinner"></span>}
                {creating ? "Creating..." : "Create Project"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

export default ProjectList;
